import * as THREE from 'three';
import { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';

type LavaLampUniforms = {
  iTime: { value: number };
  iResolution: { value: THREE.Vector3 };
  uColors: { value: THREE.Color[] };
  uGrain: { value: number };
  uSaturation: { value: number };
  uContrast: { value: number };
};

const fragmentShader = `
  precision highp float;

  uniform float iTime;
  uniform vec3 iResolution; // (width, height, 1)
  uniform vec3 uColors[4];
  uniform float uGrain;
  uniform float uSaturation;
  uniform float uContrast;

  // Hash and noise utilities
  float hash21(vec2 p) {
    p = fract(p * vec2(123.34, 345.45));
    p += dot(p, p + 34.345);
    return fract(p.x * p.y);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    float a = hash21(i);
    float b = hash21(i + vec2(1.0, 0.0));
    float c = hash21(i + vec2(0.0, 1.0));
    float d = hash21(i + vec2(1.0, 1.0));
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
  }

  // Smooth min for blending blobs
  float smin(float a, float b, float k) {
    float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
    return mix(b, a, h) - k * h * (1.0 - h);
  }

  // Palette helper
  vec3 sat(vec3 c, float s) {
    float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
    return mix(vec3(l), c, s);
  }

  vec3 tonemap(vec3 c, float k) {
    c = (c - 0.5) * k + 0.5;
    return clamp(c, 0.0, 1.0);
  }

  // Traditional lava lamp palette (magenta, electric blue, orange, red, green)
  vec3 paletteColor(int idx) {
    if (idx == 0) return vec3(0.957, 0.118, 0.510);     // magenta #f41e82
    else if (idx == 1) return vec3(0.000, 0.760, 1.000); // electric blue #00c2ff
    else if (idx == 2) return vec3(1.000, 0.541, 0.000); // orange #ff8a00
    else if (idx == 3) return vec3(1.000, 0.231, 0.231); // classic red #ff3b3b
    else return vec3(0.196, 1.000, 0.518);               // green #32ff84
  }

  vec3 cycleColor(float time) {
    // Very slow cycle for elegant transitions (~50s per color step)
    float speed = 0.02;
    float T = time * speed;
    float seg = floor(T);
    float f = smoothstep(0.0, 1.0, fract(T));
    int i0 = int(mod(seg, 5.0));
    int i1 = int(mod(seg + 1.0, 5.0));
    vec3 c0 = paletteColor(i0);
    vec3 c1 = paletteColor(i1);
    return mix(c0, c1, f);
  }

  void main() {
    vec2 fragCoord = gl_FragCoord.xy;
    vec2 R = iResolution.xy;
    vec2 uv = fragCoord / R;
    vec2 p = (fragCoord - 0.5 * R) / R.y;

    float t = iTime * 0.25;

    // Animated blob centers (elegant, slow, orchestral motion)
    vec2 c1 = 0.60 * vec2(sin(t * 0.9), cos(t * 1.1));
    vec2 c2 = 0.55 * vec2(sin(t * 1.3 + 1.7), cos(t * 0.7 + 0.9));
    vec2 c3 = 0.65 * vec2(sin(t * 0.6 + 2.4), cos(t * 1.6 + 1.3));
    vec2 c4 = 0.50 * vec2(sin(t * 1.0 + 3.1), cos(t * 0.9 + 2.6));

    float r1 = 0.70, r2 = 0.62, r3 = 0.68, r4 = 0.58;

    float f1 = r1 / length(p - c1);
    float f2 = r2 / length(p - c2);
    float f3 = r3 / length(p - c3);
    float f4 = r4 / length(p - c4);

    // Smoothly blend fields
    float field = f1;
    field = smin(field, f2, 0.35);
    field = smin(field, f3, 0.35);
    field = smin(field, f4, 0.35);

    // Organic modulation with fBm style noise
    float n = 0.0;
    n += noise(uv * 2.0 + t * 0.3) * 0.5;
    n += noise(uv * 5.0 - t * 0.5) * 0.25;
    n += noise(uv * 9.0 + t * 0.8) * 0.125;
    field += n * 0.35;

    // Soft thresholding for the blob body
    float mask = smoothstep(0.85, 1.15, field);

    // Edge glow
    float edge = smoothstep(1.05, 1.20, field) - smoothstep(1.20, 1.35, field);

    // Base gradient background
    vec3 base = mix(uColors[0], uColors[1], uv.y);
    base = mix(base, uColors[2], 0.25 + 0.25 * sin(t * 0.7 + uv.x * 3.1415));

    // Slow color cycle for classic lava lamp hues
    vec3 blobBase = cycleColor(iTime);
    base = mix(base, blobBase, 0.06); // subtle global tint for cohesion

    // Blob color follows the cycling palette
    vec3 blobCol = blobBase;
    vec3 col = mix(base, blobCol, mask);

    // Subtle rim lighting
    col += edge * (uColors[3] * 0.7 + vec3(0.05, 0.03, 0.08));

    // Varying micro-contrast via grain
    float grain = hash21(fragCoord + iTime) * uGrain;
    col += (grain - uGrain * 0.5) * 0.25;

    // Post palette touches
    col = sat(col, uSaturation);
    col = tonemap(col, uContrast);

    gl_FragColor = vec4(col, 1.0);
  }
`;

export function LavaLamp() {
  const { size, viewport } = useThree();
  const meshRef = useRef<THREE.Mesh>(null);

  const uniforms = useMemo<LavaLampUniforms>(() => {
    return {
      iTime: { value: 0 },
      iResolution: { value: new THREE.Vector3(size.width, size.height, 1) },
      // A refined, luxurious palette (deep ink to electric magenta)
      uColors: {
        value: [
          new THREE.Color('#0b0b0f'),
          new THREE.Color('#141621'),
          new THREE.Color('#2a174a'),
          new THREE.Color('#f41e82')
        ]
      },
      uGrain: { value: 0.12 },
      uSaturation: { value: 1.12 },
      uContrast: { value: 1.06 }
    };
  }, [size.width, size.height]);

  // Update resolution on resize
  if (uniforms.iResolution.value.x !== size.width || uniforms.iResolution.value.y !== size.height) {
    uniforms.iResolution.value.set(size.width, size.height, 1);
  }

  useFrame((state) => {
    uniforms.iTime.value = state.clock.getElapsedTime();
  });

  const planeScale = useMemo<[number, number, number]>(() => {
    return [viewport.width, viewport.height, 1];
  }, [viewport.width, viewport.height]);

  return (
    <mesh ref={meshRef} scale={planeScale} position={[0, 0, 0]}>
      <planeGeometry args={[1, 1, 1, 1]} />
      <shaderMaterial
        uniforms={uniforms as unknown as Record<string, THREE.IUniform>}
        fragmentShader={fragmentShader}
        vertexShader={/* glsl */`
          precision highp float;
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `}
        transparent={false}
      />
    </mesh>
  );
}


